import React, { ReactNode, forwardRef } from "react";

import classnames from "classnames";
import { twMerge } from "tailwind-merge";

export type AlertColor = "info" | "success" | "warning" | "error";
export type AlertVariant = "outline" | "dash" | "soft";
export type AlertDirection = "horizontal" | "vertical" | "responsive";

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  action?: ReactNode;
  children?: ReactNode;
  color?: AlertColor;
  description?: ReactNode;
  direction?: AlertDirection;
  icon?: ReactNode;
  title?: ReactNode;
  // variant?: AlertVariant;
}

const colorClasses: Record<AlertColor, string> = {
  info: "si-alert-info",
  success: "si-alert-success",
  warning: "si-alert-warning",
  error: "si-alert-error",
};

// Part of DaisyUI v5, will not work yet.
const variantClasses: Record<AlertVariant, string> = {
  outline: "si-alert-outline",
  dash: "si-alert-dash",
  soft: "si-alert-soft",
};

const directionClasses: Record<AlertDirection, string> = {
  horizontal: "si-alert-horizontal",
  vertical: "si-alert-vertical",
  responsive: "si-alert-vertical sm:si-alert-horizontal",
};

/**
 * Cobalt Alert - Based on DaisyUI alert classes
 * @see https://daisyui.com/components/alert/
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ action, children, className, color = "info", description, direction = "horizontal", icon, role, title, variant, ...rest }, ref) => {
    const alertRole = role || "alert";
    const hasStructuredContent = title || description;
    const classes = twMerge("si-alert", colorClasses[color], directionClasses[direction], classnames(variant && variantClasses[variant]), className);

    return (
      <div ref={ref} role={alertRole} className={classes} {...rest}>
        {icon}
        {hasStructuredContent ? (
          <div>
            {title && <h3 className="font-bold">{title}</h3>}
            {description && <div className="text-xs">{description}</div>}
          </div>
        ) : (
          children
        )}
        {action}
      </div>
    );
  },
);

Alert.displayName = "Alert";

export default Alert;
